import * as React from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FormikProps } from 'formik';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import * as Device from 'react-device-detect';

import { RenderMarkdown } from '@components';
import { TERMS_OF_SERVICE_UPDATE_DATE } from '@utils';

interface FormFieldProps {
  formik: FormikProps<any>;
  disabled?: boolean;
}

export function TermOfServiceDialog(props: Readonly<FormFieldProps>) {
  const { formik, disabled = false } = props;
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation('cta');

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleRefuse = () => {
    formik.setFieldValue('isAcceptedTOS', false);
    handleClose();
  };
  const handleAccept = () => {
    formik.setFieldValue('isAcceptedTOS', true);
    handleClose();
  };

  const descriptionElementRef = React.useRef<HTMLElement>(null);

  // Focus the description element when the dialog is opened
  React.useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  React.useEffect(() => {
    if (disabled) {
      formik.setFieldError('isAcceptedTOS', undefined);
      formik.setFieldValue('isAcceptedTOS', false);
      formik.setTouched({ ...formik.touched, isAcceptedTOS: false });
    }
  }, [disabled]);

  return (
    <React.Fragment>
      <Button
        onClick={handleClickOpen}
        size="small"
        color="uncolored"
        variant="contained"
        disabled={disabled}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {t('read & accept')}
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        scroll="paper"
        fullScreen={Device.isMobile}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
      >
        <DialogTitle id="scroll-dialog-title">
          <Typography variant="h5" color="text.primary" fontWeight={600}>
            {t('signup.legalDialog.General Conditions of Use', { ns: 'authentication' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('signup.legalDialog.Update date', {
              ns: 'authentication',
              date: moment(TERMS_OF_SERVICE_UPDATE_DATE).format('LL'),
            })}
          </Typography>
        </DialogTitle>
        <DialogContent dividers={true}>
          <RenderMarkdown fileName="termOfService" />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleRefuse}
            color="uncolored"
            variant="outlined"
            size={Device.isMobile ? 'small' : 'medium'}
          >
            {t('reject')}
          </Button>
          <Button
            onClick={handleAccept}
            color="success"
            variant="contained"
            size={Device.isMobile ? 'small' : 'medium'}
          >
            {t('accept')}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
